import { Check, Copy, Loader2 } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import React, { useState } from "react"
import ExpirationTimer from "../../pages/Devices/components/ExpirationTimer"
import { cn } from "../../utils/cn"

export interface PixPayload {
    qrcode: string
    txid: string
    expiresAt: number
}

/**
 * QR code PIX + "copia e cola" + contador de expiração + estado "aguardando".
 * Usado na ativação de acesso (PairRow) e na compra de créditos (CreditsPage).
 */
const PixPayment: React.FC<{
    pix: PixPayload
    size?: number
    hint?: React.ReactNode
    onExpire?: () => void
    className?: string
}> = ({ pix, size = 160, hint, onExpire, className }) => {
    const [copied, setCopied] = useState(false)

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(pix.qrcode)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch (err) {
            console.error("Erro ao copiar PIX:", err)
        }
    }

    const isMock = pix.txid.startsWith("MOCK")

    return (
        <div className={cn("flex flex-col items-center gap-3", className)}>
            <div className="relative rounded-xl border-2 border-primary bg-white p-4 shadow-lg">
                <QRCodeSVG
                    value={pix.qrcode}
                    size={size}
                    bgColor="#fff"
                    fgColor="#000"
                    level="L"
                    includeMargin={false}
                />
                {isMock && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-warning px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        simulação
                    </span>
                )}
            </div>

            <div className="flex items-center gap-2 text-xs text-muted">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span>
                    Aguardando pagamento · expira em{" "}
                    <ExpirationTimer
                        key={pix.txid}
                        expiresAt={pix.expiresAt}
                        onExpire={onExpire}
                    />
                </span>
            </div>

            <button
                type="button"
                onClick={copy}
                title="Copiar código PIX (copia e cola)"
                className="flex w-full max-w-md items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 p-2.5 text-left text-xs text-text transition hover:bg-primary/20 active:scale-[0.99]"
            >
                {copied ? (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success-text" />
                ) : (
                    <Copy className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                )}
                <span className="min-w-0 flex-1 break-all font-mono leading-relaxed">
                    {copied ? "Código PIX copiado!" : pix.qrcode}
                </span>
            </button>

            {hint && (
                <span className="text-center text-xs text-muted">{hint}</span>
            )}
        </div>
    )
}

export default PixPayment
